import { Injectable } from '@angular/core';
import { HttpContext } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import {
  BestMatchingProfileListItemResponse,
  CandidateAvailability,
  CreatedResponse,
  CreateRequest,
  CustomerAssignmentFilterListsResponse,
  CustomerDepartmentService,
  CustomerInquiryCreationResponse,
  CustomerInquiryFilterListsResponse,
  CustomerInquiryListItemResponse,
  CustomerInquiryOfferListItemResponse,
  CustomerInquiryService,
  CustomerService,
  DefaultDeliveryTimeListItemResponse,
  DocumentCreationResponse,
  DocumentUploadRequest,
  EmptySuccessfulResponse,
  GeneralApiResponse,
  InquiryCreateRequest,
  InquiryDate,
  InquiryDateResponse,
  InquiryDocumentResponse,
  InquiryOfferAcceptRequest,
  InquiryOfferCreateRequest,
  InquiryDeclineRequest,
  InquiryOfferDeclineRequest,
  InquiryOfferUpdateRequest,
  InquiryResponse,
  InquiryUpdateStatusRequest,
  OfferPositionResponse,
  PaginatedApiResponse,
  PaginatedApiResponseWithFilters,
  ProfileAbsenceResponse,
  ProfileOfferPositionResponse,
  PspBranchShortInfoResponse,
  PspCustomerService,
  PspInquiryListItemResponse,
  PspInquiryOfferListItemResponse,
  PspInquiryService,
  PspOfferService,
  QualificationPricingListResponse,
  RequirementCategoryResponse,
  RequirementResponse,
  RevocationRequest,
  SendImmediatelyRequest,
  UpdateRequest2,
  WorkCouncilStatus,
} from '../../../../api';
import { BaseDateService } from '../../../shared/services/base-date.service';
import { BaseSharedHelperService } from '../../../shared/services/base-shared-helper.service';
import {
  BaseInquiryShiftsUpperCase,
  BaseInquiryShiftsWithFlex,
  BaseInquiryShiftsWithFlexType,
  BaseOfferStatus,
  BaseWeekDays,
  BaseInquiryShiftsType,
} from '../_enums/base-inquiry.enum';
import { BaseAbsencePayloadTypeEnum } from '../../../shared/enums/base-absence-type.enum';
import { BasePspInquiryOfferShiftInterface } from '../_interfaces/base-psp-inquiry-offer.interface';
import { BaseInquiryCandidateShiftInterface } from '../_interfaces/base-inquiry-candidate.interface';
import { getWeekDayFromDate } from '../../../shared/helpers/base-get-weekday-from-date';
import {
  BaseListPagination,
  InquiryBestCandidatesPagination,
  InquiryOffersPagination,
} from '../../../shared/enums/base-list-pagination.enum';
import { InquiryCreateRequestExtended } from '../_models/base-inquiry-dates-form';
import { BaseDeliveryTimeListItemExtendedInterface } from '../_interfaces/base-delivery-time-list-item-extended.interface';
import { UserShortInfoNameType } from '../../candidates/_types/base-user-full-name.type';

@Injectable({ providedIn: 'root' })
export class BaseInquiryService {
  toggleInquirySidebarSubject$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  inquirySubject$: BehaviorSubject<Partial<InquiryResponse>> = new BehaviorSubject<Partial<InquiryResponse>>({});

  constructor(
    private customerInquiryService: CustomerInquiryService,
    private customerService: CustomerService,
    private pspInquiryService: PspInquiryService,
    private pspOfferService: PspOfferService,
    private pspCustomerService: PspCustomerService,
    private customerDepartmentService: CustomerDepartmentService
  ) {}

  getInquiries(params: {
    customer: number;
    page?: any;
    size?: any;
    status?: any;
    mainQualifications?: any;
    customerDepartments?: any;
    customerLocations?: any;
    includeDates?: boolean;
    current?: boolean;
    search?: string;
    createdBy?: any;
    order?: any;
  }): Observable<
    PaginatedApiResponseWithFilters & {
      data?: Array<CustomerInquiryListItemResponse>;
      filters?: CustomerInquiryFilterListsResponse;
    }
  > {
    return this.customerInquiryService.getCustomerListOfInquiries({
      customer: params.customer,
      page: params.page || BaseListPagination.page,
      size: params.size || BaseListPagination.size,
      'statuses[]': params.status,
      'mainQualifications[]': params.mainQualifications,
      'customerDepartments[]': params.customerDepartments,
      'customerLocations[]': params.customerLocations,
      includeDates: params.includeDates,
      current: params.current,
      createdBy: params.createdBy,
      search: params.search,
      orderKey: params.order?.split(',')[0],
      orderDirection: params.order?.split(',')[1],
    });
  }

  createInquiry(
    customer: number,
    body: InquiryCreateRequestExtended
  ): Observable<
    GeneralApiResponse & {
      data?: CustomerInquiryCreationResponse;
    }
  > {
    return this.customerInquiryService
      .postInquiryCreate({ customer, body: body as InquiryCreateRequest })
      .pipe(take(1));
  }

  getPSPInquiries(params: {
    psp: number;
    page?: any;
    size?: any;
    status?: any;
    customers?: any[];
    customerDepartments?: any[];
    pspBranches?: any;
    mainQualifications?: any[];
    order?: any;
  }): Observable<PaginatedApiResponse & { data?: PspInquiryListItemResponse[] }> {
    return this.pspInquiryService.getPspListOfInquiries({
      psp: params.psp,
      page: params.page || BaseListPagination.page,
      size: params.size || BaseListPagination.size,
      status: params.status,
      'customers[]': params.customers,
      'customerDepartments[]': params.customerDepartments,
      'pspBranches[]': params.pspBranches,
      'mainQualifications[]': params.mainQualifications,
      orderKey: params.order?.split(',')[0],
      orderDirection: params.order?.split(',')[1],
    });
  }

  ungroupRequirements(groupedRequirements: RequirementCategoryResponse[] = []): RequirementResponse[] {
    return groupedRequirements.flatMap((item) => item.requirements);
  }

  allRequirementsFulfilled(
    offerProfileRequirements: RequirementCategoryResponse[],
    inquiryRequirements: RequirementCategoryResponse[]
  ): boolean {
    const profileReqIds = new Set(this.ungroupRequirements(offerProfileRequirements).map((req) => req.id));
    const inquiryReqs = this.ungroupRequirements(inquiryRequirements);

    return inquiryReqs.every((req) => profileReqIds.has(req.id));
  }

  getInquiryWeekdays(dates: InquiryDateResponse[]): Record<BaseWeekDays, boolean> {
    const weekdays = {
      [BaseWeekDays.MO]: false,
      [BaseWeekDays.TU]: false,
      [BaseWeekDays.WE]: false,
      [BaseWeekDays.TH]: false,
      [BaseWeekDays.FR]: false,
      [BaseWeekDays.SA]: false,
      [BaseWeekDays.SU]: false,
    };

    let trueCount = 0;

    for (const dateInfo of dates) {
      const day = getWeekDayFromDate(dateInfo.date);

      if (!weekdays[day as BaseWeekDays]) {
        weekdays[day as BaseWeekDays] = true;
        trueCount++;
      }

      if (trueCount === 7) break;
    }

    return weekdays;
  }

  /**
   * Returns a weekdays record with every day selected, in the same
   * Record<BaseWeekDays, boolean> shape as getInquiryWeekdays. Used when extending/inquiring a
   * specific employee, where all weekdays are selected by default — a full selection always stays
   * valid against the new date range and never trips the invalidSelectedDays validator (KS-6927).
   */
  getAllWeekdays(): Record<BaseWeekDays, boolean> {
    return {
      [BaseWeekDays.MO]: true,
      [BaseWeekDays.TU]: true,
      [BaseWeekDays.WE]: true,
      [BaseWeekDays.TH]: true,
      [BaseWeekDays.FR]: true,
      [BaseWeekDays.SA]: true,
      [BaseWeekDays.SU]: true,
    };
  }

  getBestCandidatesByInquiry(params: {
    psp: number;
    inquiry: number;
    page?: number;
    size?: number;
    search?: string;
    qualifications?: any;
    order?: any;
  }): Observable<
    PaginatedApiResponse & {
      data?: Array<BestMatchingProfileListItemResponse>;
    }
  > {
    return this.pspInquiryService.getAppApiWebV1PspInquiryProfileListIndex({
      psp: params.psp,
      inquiry: params.inquiry,
      search: params.search,
      page: params.page || InquiryBestCandidatesPagination.page,
      size: params.size || InquiryBestCandidatesPagination.size,
      'mainQualifications[]': params.qualifications,
      orderKey:
        params.order?.split(',')[0] === 'qualificationName'
          ? 'profileMainQualificationName'
          : params.order?.split(',')[0],
      orderDirection: params.order?.split(',')[1],
    });
  }

  getOffersByInquiry(params: {
    psp: number;
    inquiry: number;
    size?: number;
    page?: number;
    search?: string;
    status?: '0' | '2' | '3' | '4' | '5' | '6';
    experience?: boolean;
    qualifications?: any;
    order?: any;
  }): Observable<
    PaginatedApiResponse & {
      data?: Array<PspInquiryOfferListItemResponse>;
    }
  > {
    return this.pspOfferService.getPspInquiryOffersList({
      psp: params.psp,
      inquiry: params.inquiry,
      search: params.search,
      page: params.page || InquiryOffersPagination.page,
      size: params.size || InquiryOffersPagination.size,
      status: params.status,
      'mainQualifications[]': params.qualifications,
      experience: params.experience,
      orderKey: params.order?.split(',')[0],
      orderDirection: params.order?.split(',')[1],
    });
  }

  publishInquiry(inquiry: number, customer: number, body: InquiryUpdateStatusRequest): Observable<any> {
    return this.customerInquiryService.patchCustomerInquiryUpdateStatus({
      inquiry,
      customer,
      body,
    });
  }

  getPSPInquiry(params: { inquiry: number; psp: number }): Observable<InquiryResponse> {
    return this.pspInquiryService.getPspInquiryShow(params).pipe(map((response) => response.data));
  }

  getInquiryProviders(params: {
    customer: number;
    inquiry: number;
  }): Observable<BaseDeliveryTimeListItemExtendedInterface[]> {
    return this.customerInquiryService
      .getCustomerInquiryDeliveryTimeList(params)
      .pipe(
        map((response) =>
          response.data.map((item) => ({ ...item, isSelected: false } as BaseDeliveryTimeListItemExtendedInterface))
        )
      );
  }

  sendProvidersImmediately(params: {
    customer: number;
    inquiry: number;
    body: SendImmediatelyRequest;
  }): Observable<EmptySuccessfulResponse> {
    return this.customerInquiryService.postCustomerInquiryDeliveryTimesSendImmediately(params);
  }

  createdOfferPositionExists(
    date: string,
    offerPositions: OfferPositionResponse[] = [],
    currentShift: string
  ): boolean {
    const offerPosition = offerPositions.find((pos) => pos.date === date);
    return !!offerPosition && this.checkShiftType(currentShift, offerPosition);
  }

  acceptedOfferPositionExists(date: string, offer: PspInquiryOfferListItemResponse, currentShift: string): boolean {
    const isOfferConfirmed = offer.status === BaseOfferStatus.confirmed;
    return (
      this.areOfferPositionsExist(date, offer, currentShift) &&
      isOfferConfirmed &&
      (!offer.assignment?.assignmentCancellation?.cancelationDate ||
        (!!offer.assignment?.assignmentCancellation?.cancelationDate &&
          BaseDateService.isBefore(
            new Date(date),
            new Date(offer.assignment?.assignmentCancellation?.cancelationDate)
          )))
    );
  }

  isCancelledShiftForDate(date: string, offer: PspInquiryOfferListItemResponse, currentShift: string): boolean {
    const isOfferConfirmed = offer.status === BaseOfferStatus.confirmed;
    const currentDateOfferPosition = offer.positions?.find((pos) => pos.date === date);

    return (
      isOfferConfirmed &&
      this.areOfferPositionsExist(date, offer, currentShift) &&
      !!currentDateOfferPosition?.isCanceledAssignmentDate
    );
  }

  offerPositionIsAcceptedButNotConfirmed(
    date: string,
    offer: PspInquiryOfferListItemResponse,
    currentShift: string
  ): boolean {
    const isOfferNeedsConfirmation = offer.status === BaseOfferStatus.needsConfirmation;
    return this.areOfferPositionsExist(date, offer, currentShift) && isOfferNeedsConfirmation;
  }

  checkAvailabilityByDirectDay(currentDate: string, availability: CandidateAvailability): boolean {
    return !!availability[
      BaseDateService.format(new Date(currentDate), 'EEE').toLowerCase() as keyof CandidateAvailability
    ];
  }

  checkAvailabilityByDateAndBookedPositions(currentDate: string, availability: CandidateAvailability): boolean {
    return !!availability[
      BaseDateService.format(new Date(currentDate), 'EEE').toLowerCase() as keyof CandidateAvailability
    ];
  }

  checkAvailabilityForDirectShift(shift: string, availability: CandidateAvailability): boolean {
    if (shift === BaseInquiryShiftsWithFlex.flex) {
      return true;
    }
    return !!availability[shift as keyof CandidateAvailability];
  }

  isHolidayForDate(date: string, absences: Array<ProfileAbsenceResponse>): boolean {
    return this.isAbsentForDateByAbsenceType(BaseAbsencePayloadTypeEnum.holiday, date, absences);
  }

  isOtherForDate(date: string, absences: Array<ProfileAbsenceResponse>): boolean {
    return this.isAbsentForDateByAbsenceType(BaseAbsencePayloadTypeEnum.other, date, absences);
  }

  isTimeOffForDate(date: string, absences: Array<ProfileAbsenceResponse>): boolean {
    return this.isAbsentForDateByAbsenceType(BaseAbsencePayloadTypeEnum.timeOffCompensation, date, absences);
  }

  isUnexcusedForDate(date: string, absences: Array<ProfileAbsenceResponse>): boolean {
    return this.isAbsentForDateByAbsenceType(BaseAbsencePayloadTypeEnum.unexcusedAbsence, date, absences);
  }

  isFreeShiftForDate(date: string, absences: Array<ProfileAbsenceResponse>): boolean {
    return this.isAbsentForDateByAbsenceType(BaseAbsencePayloadTypeEnum.freeShift, date, absences);
  }

  isAssignedOutsideForDate(date: string, absences: Array<ProfileAbsenceResponse>): boolean {
    return this.isAbsentForDateByAbsenceType(BaseAbsencePayloadTypeEnum.offline, date, absences);
  }

  isIllForDate(date: string, absences: Array<ProfileAbsenceResponse>): boolean {
    return this.isAbsentForDateByAbsenceType(BaseAbsencePayloadTypeEnum.illness, date, absences);
  }

  isAbsentForDate(date: string, absences: Array<ProfileAbsenceResponse>): boolean {
    return !!absences.find((absence) => {
      return (
        (BaseDateService.isBefore(new Date(date), new Date(absence.endDate)) &&
          BaseDateService.isAfter(new Date(date), new Date(absence.startDate))) ||
        date === absence.startDate ||
        date === absence.endDate
      );
    });
  }

  offerPositionExistsAndSelected(
    date: string,
    offerPositions: ProfileOfferPositionResponse[] = [],
    currentShift: string,
    inquiryId: number,
    _inquiryDates: InquiryDate[]
  ): boolean {
    const offerPosition = offerPositions.find((pos) => pos.date === date);

    // BE SHOULD IMPROVE OFFER POSITION AND ADD SELECTED PROP TO IT
    // @ts-ignore
    return (
      !!offerPosition &&
      offerPosition.selectedShift &&
      !offerPosition.isCanceledAssignmentDate &&
      this.checkShiftType(currentShift, offerPosition) &&
      offerPosition.inquiryId?.toString() === inquiryId.toString()
    );
  }

  offerPositionExistsAndSelectedForOtherInquiry(
    date: string,
    offerPositions: ProfileOfferPositionResponse[] = [],
    currentShift: string,
    inquiryId: number
  ): boolean {
    const offerPosition = offerPositions.find((pos) => pos.date === date);

    return (
      !!offerPosition &&
      !!offerPosition.selectedShift &&
      !offerPosition.isCanceledAssignmentDate &&
      this.checkShiftType(currentShift, offerPosition) &&
      offerPosition.inquiryId?.toString() !== inquiryId.toString()
    );
  }

  inquiryDateExists(
    date: string,
    inquiryDates: InquiryDateResponse[],
    shift: BaseInquiryShiftsWithFlexType,
    deselectedItems: { shift: string; date: string }[]
  ): boolean {
    const optimizedDeselectedItems = deselectedItems.filter((item) => item.shift === shift);

    if (shift === BaseInquiryShiftsWithFlex.flex) {
      return Object.values(BaseInquiryShiftsUpperCase).some(
        (item) => inquiryDates.find((value) => value.date === date && value.flexShift)?.[item] ?? false
      );
    } else {
      return !!inquiryDates.find((inquiryDate) => {
        return (
          inquiryDate.date === date &&
          !!inquiryDate[BaseInquiryShiftsUpperCase[shift]] &&
          !optimizedDeselectedItems.find((item) => item.date === inquiryDate.date)
        );
      });
    }
  }

  getCustomerInquiryOffers(params: {
    customer: number;
    inquiry: number;
    page?: any;
    size?: any;
    search?: string;
    status?: '0' | '5' | '100';
    workCouncilStatus?: WorkCouncilStatus;
    mainQualifications?: any;
    pspBranches?: any;
    profileAverageRating?: number;
    experience?: boolean;
    order?: any;
    pinnedOfferId?: number;
  }): Observable<
    PaginatedApiResponse & {
      data?: Array<CustomerInquiryOfferListItemResponse>;
      filters?: CustomerAssignmentFilterListsResponse;
    }
  > {
    return this.customerInquiryService.getCustomerInquiryOffersList({
      customer: params.customer,
      inquiry: params.inquiry,
      page: params.page || InquiryOffersPagination.page,
      size: params.size || InquiryOffersPagination.size,
      search: params.search,
      status: params.status,
      workCouncilStatus: params.workCouncilStatus,
      'mainQualifications[]': params.mainQualifications,
      'pspBranches[]': params.pspBranches,
      profileAverageRating: params.profileAverageRating,
      experience: params.experience,
      orderKey: params.order?.split(',')[0],
      orderDirection: params.order?.split(',')[1],
      pinnedOfferId: params.pinnedOfferId,
    });
  }

  updateInquiryStatus(inquiry: number, customer: number, status: 1 | 2 | 3, closedNote?: string): Observable<any> {
    return this.customerInquiryService.patchCustomerInquiryUpdateStatus({
      inquiry,
      customer,
      body: { status, closedNote },
    });
  }

  getCustomerInquiry(customer: number, inquiry: number): Observable<InquiryResponse> {
    return this.customerInquiryService
      .getCustomerInquiryShow({ customer, inquiry })
      .pipe(map((response) => response.data));
  }

  getContractDocument(customer: number): Observable<any> {
    return this.customerService.getCustomerContractTemplateDownload$Response({ customer }).pipe(
      map((response) => {
        const contentDispositionValue = response.headers.get('Content-Disposition');
        const fileName = BaseSharedHelperService.fileNameFromHeaderValue(contentDispositionValue, '=');
        return {
          body: response.body,
          fileName,
        };
      })
    );
  }

  getCustomerInquiryOfferDocumentFileDownload(params: {
    customer: number;
    inquiry: number;
    offer: number;
    document: string;
    context?: HttpContext;
  }): Observable<any> {
    return this.customerInquiryService.getCustomerInquiryOfferDocumentFileDownload$Response(params).pipe(
      map((response) => {
        const contentDispositionValue = response.headers.get('Content-Disposition');
        const fileName = BaseSharedHelperService.fileNameFromHeaderValue(contentDispositionValue, '=');
        return {
          body: response.body,
          fileName,
        };
      })
    );
  }

  responsibleUsersList(params: { psp: number; inquiry: number }): Observable<UserShortInfoNameType[]> {
    return this.pspInquiryService.getPspInquiryResponsibleList(params).pipe(
      map((response) =>
        (response.data || []).map((user: any) => {
          user.fullName = `${user.firstName} ${user.lastName}`;
          return user;
        })
      )
    );
  }

  createInquiryOffer(params: {
    psp: number;
    inquiry: number;
    body: InquiryOfferCreateRequest;
  }): Observable<CreatedResponse> {
    return this.pspOfferService.postPspInquiryOffersCreate(params);
  }

  updateInquiryOffer(params: {
    offer: number;
    psp: number;
    inquiry: number;
    body: InquiryOfferUpdateRequest;
  }): Observable<EmptySuccessfulResponse> {
    return this.pspOfferService.putPspInquiryOffersUpdate(params);
  }

  pspCustomerQualificationPrice(params: {
    psp: number;
    customer: number;
    qualification: number;
    location: number;
  }): Observable<QualificationPricingListResponse> {
    return this.pspCustomerService
      .getAppApiWebV1PspCustomerPriceQualificationListIndex(params)
      .pipe(map((response) => response.data));
  }

  acceptOffer(
    offer: number,
    customer: number,
    inquiry: number,
    body: InquiryOfferAcceptRequest
  ): Observable<EmptySuccessfulResponse> {
    return this.customerInquiryService.patchCustomerInquiryOfferAccept({
      offer,
      customer,
      inquiry,
      body,
    });
  }

  getPspCustomerContractTemplateFile(params: { psp: number; customer: number }): Observable<any> {
    return this.pspCustomerService.getPspCustomerContractTemplateDownload$Response(params).pipe(
      map((response) => {
        const contentDispositionValue = response.headers.get('Content-Disposition');
        const fileName = BaseSharedHelperService.fileNameFromHeaderValue(contentDispositionValue, '=');
        return {
          body: response.body,
          fileName,
        };
      })
    );
  }

  declineOfferCustomer(params: {
    customer: number;
    offer: number;
    inquiry: number;
    body: InquiryOfferDeclineRequest;
  }): Observable<EmptySuccessfulResponse> {
    return this.customerInquiryService.putCustomerInquiryOfferDecline(params);
  }

  /**
   * PSP declines an inquiry for every branch of the logged-in user. Permanent — cannot be undone.
   * The inquiry stays open for other branches and every other PSP.
   */
  declineInquiry(params: {
    psp: number;
    inquiry: number;
    body: InquiryDeclineRequest;
  }): Observable<EmptySuccessfulResponse> {
    return this.pspInquiryService.putPspInquiryDecline(params);
  }

  revokeOffer(
    psp: number,
    inquiry: number,
    offer: number,
    body: RevocationRequest
  ): Observable<EmptySuccessfulResponse> {
    return this.pspOfferService.postPspInquiryOffersRevoke({
      psp,
      inquiry,
      offer,
      body,
    });
  }

  updateCustomerInquiry(params: {
    customer: number;
    inquiry: string;
    body: InquiryCreateRequestExtended;
  }): Observable<EmptySuccessfulResponse> {
    return this.customerInquiryService.putCustomerInquiryUpdate({
      customer: params.customer,
      inquiry: params.inquiry,
      body: params.body as InquiryCreateRequest,
    });
  }

  getPspInquiryBranchList(orgId: number, inquiry: number): Observable<Array<PspBranchShortInfoResponse>> {
    return this.pspInquiryService
      .getPspInquiryBranchList({ psp: orgId, inquiry })
      .pipe(map((response) => response.data));
  }

  checkIsFlexShiftsExist(positions: OfferPositionResponse[] | InquiryDateResponse[]): boolean {
    return (positions || []).some((position) => position.flexShift === true);
  }

  checkIsShiftsExist(
    shiftType: BaseInquiryShiftsType,
    positions: OfferPositionResponse[] | InquiryDateResponse[],
    isFlex = false
  ): boolean {
    return positions.some((position) => {
      return isFlex
        ? !position.flexShift && position[BaseInquiryShiftsUpperCase[shiftType]]
        : position[BaseInquiryShiftsUpperCase[shiftType]];
    });
  }

  isBubbleDotVisible(
    shifts: BaseInquiryCandidateShiftInterface[] | BasePspInquiryOfferShiftInterface[],
    shiftType: BaseInquiryShiftsWithFlexType
  ): boolean {
    const currentShift = shifts.find((item) => item.shift === shiftType);
    if (!currentShift?.index) {
      return false;
    }
    const bubblesToCheck = shifts.filter((item) => (item.index as number) < (currentShift.index as number));
    return (
      bubblesToCheck.some((bubble) => bubble.currentValue === currentShift.currentValue) ||
      (shiftType !== BaseInquiryShiftsWithFlex.flex && shifts.every((item) => item.isFlexibleShift))
    );
  }

  isTwoDatesAreSimilar(inquiryDate1: InquiryDateResponse, inquiryDate2: InquiryDateResponse): boolean {
    if (inquiryDate1.flexShift !== inquiryDate2.flexShift) {
      return false;
    }

    return Object.values(BaseInquiryShiftsUpperCase).every((shift) => inquiryDate1[shift] === inquiryDate2[shift]);
  }

  preselectCustomerInquiryOffer(params: {
    customer: number;
    inquiry: number;
    offer: number;
  }): Observable<EmptySuccessfulResponse> {
    return this.customerInquiryService.postCustomerInquiryOfferPreSelection(params);
  }

  unselectCustomerInquiryOffer(params: {
    customer: number;
    inquiry: number;
    offer: number;
  }): Observable<EmptySuccessfulResponse> {
    return this.customerInquiryService.deleteCustomerInquiryOfferUnselection(params);
  }

  getSimilarInquiries(params: {
    customer: number;
    department: number;
    startDate?: string;
    endDate?: string;
    shifts?: string;
    qualifications?: string;
    mainQualification?: number;
  }): Observable<Array<CustomerInquiryListItemResponse>> {
    return this.customerDepartmentService
      .getCustomerDepartmentInquirySimilar({
        customer: params.customer,
        department: params.department,
        startDate: params.startDate,
        endDate: params.endDate,
        'shifts[]': params.shifts,
        'qualifications[]': params.qualifications,
        mainQualification: params.mainQualification,
      })
      .pipe(map((response) => response.data));
  }

  getDefaultDeliveryTimeList(params: {
    customer: number;
    inquiry: number;
  }): Observable<Array<DefaultDeliveryTimeListItemResponse>> {
    return this.customerInquiryService
      .getCustomerInquiryDeliveryTimeDefaultList({
        customer: params.customer,
        inquiry: params.inquiry,
      })
      .pipe(map((response) => response.data));
  }

  createCustomerInquiryDeliveryTimes(params: {
    customer: number;
    inquiry: number;
    body: CreateRequest;
  }): Observable<EmptySuccessfulResponse> {
    return this.customerInquiryService.postCustomerInquiryDeliveryTimesCreate({
      customer: params.customer,
      inquiry: params.inquiry,
      body: params.body,
    });
  }

  uploadInquiryDocument(params: {
    customer: number;
    body: DocumentUploadRequest;
  }): Observable<DocumentCreationResponse> {
    return this.customerInquiryService.postCustomerInquiryDocumentCreate(params).pipe(map((response) => response.data));
  }

  getInquiryDocument(params: {
    customer: number;
    inquiry: number;
    document: string;
  }): Observable<InquiryDocumentResponse> {
    return this.customerInquiryService.getCustomerInquiryDocumentShow(params).pipe(map((response) => response.data));
  }

  downloadExistingCustomerInquiryDocumentFile(params: {
    customer: number;
    inquiry: number;
    document: string;
  }): Observable<any> {
    return this.customerInquiryService.getCustomerInquiryDocumentDownload$Response(params).pipe(
      map((response) => {
        const contentDispositionValue = response.headers.get('Content-Disposition');
        const fileName = BaseSharedHelperService.fileNameFromHeaderValue(contentDispositionValue, '=');
        return {
          body: response.body,
          fileName,
        };
      })
    );
  }

  downloadExistingPSPInquiryDocumentFile(params: { psp: number; inquiry: number; document: string }): Observable<any> {
    return this.pspInquiryService.getPspInquiryDocumentDownload$Response(params).pipe(
      map((response) => {
        const contentDispositionValue = response.headers.get('Content-Disposition');
        const fileName = BaseSharedHelperService.fileNameFromHeaderValue(contentDispositionValue, '=');
        return {
          body: response.body,
          fileName,
        };
      })
    );
  }

  updateInquiryDocument(params: {
    customer: number;
    document: string;
    body: UpdateRequest2;
  }): Observable<EmptySuccessfulResponse> {
    return this.customerInquiryService.putCustomerInquiryDocumentCreationUpdate(params);
  }

  deleteInquiryDocument(params: {
    customer: number;
    inquiry: number;
    document: string;
  }): Observable<EmptySuccessfulResponse> {
    return this.customerInquiryService.deleteCustomerInquiryDocumentDelete(params);
  }

  private isAbsentForDateByAbsenceType(
    type: BaseAbsencePayloadTypeEnum,
    date: string,
    absences: Array<ProfileAbsenceResponse>
  ): boolean {
    return !!absences.find((absence) => {
      return (
        (absence.type === type &&
          BaseDateService.isBefore(new Date(date), new Date(absence.endDate)) &&
          BaseDateService.isAfter(new Date(date), new Date(absence.startDate))) ||
        (absence.type === type && date === absence.startDate) ||
        (absence.type === type && date === absence.endDate)
      );
    });
  }

  private checkShiftType(
    currentShift: string,
    inquiryDate: OfferPositionResponse | ProfileOfferPositionResponse
  ): boolean {
    switch (currentShift) {
      case BaseInquiryShiftsWithFlex.fd:
        return !!inquiryDate?.shiftFd;
      case BaseInquiryShiftsWithFlex.nd:
        return !!inquiryDate?.shiftNd;
      case BaseInquiryShiftsWithFlex.sd:
        return !!inquiryDate?.shiftSd;
      case BaseInquiryShiftsWithFlex.zd:
        return !!inquiryDate?.shiftZd;
      case BaseInquiryShiftsWithFlex.flex:
        if (inquiryDate?.flexShift) {
          return !!inquiryDate?.shiftFd || !!inquiryDate?.shiftNd || !!inquiryDate?.shiftSd || !!inquiryDate?.shiftZd;
        } else {
          return false;
        }
      default:
        return false;
    }
  }

  private areOfferPositionsExist(date: string, offer: PspInquiryOfferListItemResponse, currentShift: string): boolean {
    const offerPositions: OfferPositionResponse[] = offer.positions || [];

    const offerPosition = offerPositions.find((pos) => pos.date === date);
    return (
      !!offerPosition &&
      this.checkShiftType(currentShift, offerPosition) &&
      (offerPosition.selectedShift === currentShift || (!!offerPosition.flexShift && !!offerPosition.selectedShift))
    );
  }
}
